import { authUser } from "@origin-digital/event-dispatcher";
import { PlanFuelSelection } from "@origin-digital/sales-common";
import { logger } from "@origin-digital/reporting-client";

import {
  BUTTON_CP,
  CLOUDINARY_ENDPOINT,
  CONTENT_TYPE_MOVE,
  FUEL_DUAL,
  FUEL_ELEC,
  FUEL_GAS,
  FUEL_KEY,
  FUEL,
  IMAGE_DEFAULTS,
  INTERNET_PLANS,
  LPG_OFFERS,
  COMPARE_PLANS,
  AUTH_ERROR,
  BUSINESS_COMPARE_PLANS,
} from "../consts";

import { IAddressLookup } from "../consts/interfaces";
import { isBusinessPage } from "./pageContext";

export const getCloudinaryUrl = (
  imageSHA: string,
  quality: number = IMAGE_DEFAULTS.QUALITY
): string => {
  return `${CLOUDINARY_ENDPOINT}/q_${quality},f_auto,c_scale/${imageSHA}`;
};

export const hasPathnameMatch = (key: string) => {
  const pathnameQueryString = window.location.pathname;
  return pathnameQueryString.indexOf(key) !== -1;
};

export const getContentType = () => {
  if (hasPathnameMatch(FUEL_KEY)) {
    return FUEL;
  } else {
    return CONTENT_TYPE_MOVE;
  }
};

export const getOtherFuel = (id: string) => {
  if (id === FUEL_ELEC || id === FUEL_GAS) {
    const otherFuel = id === FUEL_ELEC ? FUEL_GAS : FUEL_ELEC;
    return otherFuel;
  }
  return id;
};

export const getFuelName = (
  id: string,
  currentFuel: PlanFuelSelection | "",
  checked: boolean
) => {
  let fuelName = "";
  if (checked && (id === FUEL_GAS || id === FUEL_ELEC)) {
    fuelName = currentFuel ? FUEL_DUAL : id;
  } else if (!checked) {
    fuelName = currentFuel === FUEL_DUAL ? getOtherFuel(id) : "";
  }
  return fuelName as PlanFuelSelection | "";
};

export const isNothingSelected = (
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean
) => {
  return !fuelValue && !lpgValue && !internetValue;
};

export const isLPGOnlySelected = (
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean
) => {
  return lpgValue && !fuelValue && !internetValue;
};

const cpDestinationPath = (
  isBusiness: boolean,
  originAddressId: string,
  fuelValue: any
) => {
  return isBusiness
    ? `/business/electricity-and-gas-plans?originAddressId=${originAddressId}&planfuel=${fuelValue}`
    : `/electricity-gas/plans.html?originAddressId=${originAddressId}&planfuel=${fuelValue}`;
};

const signUpDestinationPath = (
  isBusiness: boolean,
  originAddressId: string,
  useCart = false
) => {
  return isBusiness
    ? `/business/signup?campaignAppId=movers-widget&journeyType=Move&originAddressId=${originAddressId}&useCart=${useCart}`
    : `/sign-up?campaignAppId=movers-widget&journeyType=Move&originAddressId=${originAddressId}&useCart=${useCart}`;
};

export const getDestinationURL = (
  buttonValue: string,
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean,
  originAddressId: string,
  useCart = false
) => {
  if (isLPGOnlySelected(fuelValue, lpgValue, internetValue)) {
    return `${LPG_OFFERS}?originAddressId=${originAddressId}`;
  } else if (internetValue && !fuelValue) {
    return buttonValue === BUTTON_CP && !originAddressId
      ? `${INTERNET_PLANS}`
      : `${INTERNET_PLANS}?originAddressId=${originAddressId}`;
  } else {
    return buttonValue === BUTTON_CP
      ? cpDestinationPath(isBusinessPage(), originAddressId, fuelValue)
      : signUpDestinationPath(isBusinessPage(), originAddressId, useCart);
  }
};

export const getDestinationURLWhenNoAddress = (
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean
) => {
  // allow for lpg only and internet without fuel
  if (internetValue && !fuelValue) {
    return `${INTERNET_PLANS}`;
  }

  if (isLPGOnlySelected(fuelValue, lpgValue, internetValue)) {
    return LPG_OFFERS;
  }

  return isBusinessPage() ? BUSINESS_COMPARE_PLANS : COMPARE_PLANS;
};

export const getAddressErrorMessage = (
  errors: any,
  address: IAddressLookup,
  originAddressId: string | undefined
) => {
  const { invalid, required } = errors;

  switch (true) {
    // some failure with originAddressId
    // @ts-expect-error: addressLookup state mutates between a string and QAS Address object
    // (the case condition check here is a sufficient guard), but larger refactor needed to remodel state.
    case address["state"] && !originAddressId:
      return invalid;

    // success
    // @ts-expect-error: addressLookup state mutates between a string and QAS Address object
    // (the case condition check here is a sufficient guard), but larger refactor needed to remodel state.
    case address["state"] && !!originAddressId:
      return "";

    // empty
    default:
      return required;
  }
};

export const getCheckboxErrorMessage = (
  errors: any,
  isMovers: boolean,
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean
) => {
  const { required } = errors;

  if (isMovers) {
    if (isNothingSelected(fuelValue, lpgValue, internetValue)) {
      return required;
    }
  } else if (!fuelValue) {
    return required;
  }

  return "";
};

export const getSelectedProducts = (
  fuelValue: PlanFuelSelection | "",
  lpgValue: boolean,
  internetValue: boolean
) =>
  [
    (fuelValue === FUEL_ELEC || fuelValue === FUEL_DUAL) && "Electricity",
    (fuelValue === FUEL_GAS || fuelValue === FUEL_DUAL) && "Gas",
    lpgValue && "LPG",
    internetValue && "Broadband",
  ].filter((value) => !!value);

export const isAuthenticated = async () => {
  try {
    const { isAuth } = await authUser({});
    return isAuth;
  } catch (error) {
    logger.error(AUTH_ERROR, error);
    return false;
  }
};
